import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, CreditCard, Bitcoin, Copy, Send, ShieldAlert } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const GUIDES = {
  card: {
    title: 'How to pay by Bank Card',
    subtitle: 'A quick walkthrough for card payments',
    icon: CreditCard,
    color: 'text-blue-700 bg-blue-50 border-blue-200',
    notice: 'Due to the recent rise in online payment fraud, direct checkout has been temporarily disabled for security reasons. Please contact us via email before making any payment, and we will guide you through the payment process safely.',
    steps: [
      'Send us a message on the Subscribe page with your name, email, and the plan you want (15 or 30 days).',
      'Our team will reply within a few hours with a secure card checkout link.',
      'Complete the payment with any Visa, Mastercard, or Maestro card. 3D Secure is fully supported.',
      'Once payment is confirmed, your subscription is activated instantly — you receive access details by email.'
    ],
    notes: [
      'All payments are processed through PCI-DSS compliant providers.',
      'We never store or see your card details.',
      'International cards accepted (EUR, USD, GBP, CHF, CAD, AUD).'
    ]
  },
  crypto: {
    title: 'How to pay with Cryptocurrency',
    subtitle: 'Fast, private, and network-independent',
    icon: Bitcoin,
    color: 'text-amber-700 bg-amber-50 border-amber-200',
    steps: [
      'On the Subscribe page, choose your plan, then send us a contact message stating you want to pay in crypto.',
      'We will reply with the current wallet address for either USDT TRC20 (Tron) or USDT BEP20 (Binance Smart Chain) — whichever you prefer.',
      'Send the exact euro-equivalent USDT amount to that address. Do not send from an exchange with pending withdrawal delays.',
      'Reply to our email with the transaction hash (TxID). Access is granted within 30 minutes of on-chain confirmation.'
    ],
    notes: [
      'We accept USDT only. Please do not send BTC, ETH, or other tokens — they will not be credited.',
      'Always double-check the network (TRC20 vs BEP20) before sending.',
      'Network fees are the responsibility of the sender.'
    ]
  }
};

export default function PayGuide() {
  const { kind } = useParams();
  const guide = GUIDES[kind];

  if (!guide) {
    return (
      <>
        <Header />
        <div className="max-w-3xl mx-auto px-5 py-20 text-center">
          <h1 className="font-display text-3xl font-extrabold text-navy">Guide not found</h1>
          <p className="mt-3 text-slate-600">The guide you are looking for does not exist.</p>
          <Link to="/subscribe">
            <Button className="mt-6 bg-royal hover:bg-royal-dark text-white">Back to Subscribe</Button>
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = guide.icon;

  return (
    <>
      <Header />
      <section className="py-16 bg-gradient-to-b from-mist to-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Link to="/subscribe" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-blue-700 font-medium text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to subscribe
          </Link>

          <div className={`mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${guide.color}`}>
            <Icon className="w-3.5 h-3.5" /> Payment Guide
          </div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-extrabold text-navy tracking-tight">{guide.title}</h1>
          <p className="mt-3 text-lg text-slate-600">{guide.subtitle}</p>

          {guide.notice && (
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-xs md:text-[13px] text-amber-900 leading-relaxed">
                {guide.notice}
              </p>
            </div>
          )}

          <div className="mt-10 space-y-4">
            {guide.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-xl bg-blue-700 text-white flex items-center justify-center font-display font-extrabold flex-shrink-0">{i + 1}</div>
                <p className="text-slate-700 leading-relaxed pt-1.5">{s}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl bg-slate-900 text-white p-6 md:p-8">
            <div className="text-xs uppercase tracking-wider font-bold text-blue-300 mb-3">Good to know</div>
            <ul className="space-y-2">
              {guide.notes.map((n, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/subscribe" className="flex-1">
              <Button size="lg" className="w-full bg-royal hover:bg-royal-dark text-white font-semibold h-14 rounded-xl">
                <Send className="w-4 h-4 mr-2" /> Contact us to pay
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="h-14 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:text-blue-700 font-semibold"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(window.location.href);
                  toast({ title: 'Link copied', description: 'Guide URL copied to clipboard.' });
                } catch {}
              }}
            >
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
